import type { System } from '../../context/SystemContext'

export type MetricCategory =
    | 'temperature'
    | 'humidity'
    | 'ph'
    | 'ec'
    | 'tds'
    | 'moisture'
    | 'flow'
    | 'unknown'

const DEFAULT_BAND: Partial<Record<MetricCategory, { below: number; above: number }>> = {
    ph: { below: 0.35, above: 0.35 },
    ec: { below: 0.15, above: 0.15 },
    temperature: { below: 2, above: 2 },
    humidity: { below: 5, above: 5 },
}

export function classifyDeviceType(deviceType: string): MetricCategory {
    const t = deviceType.toLowerCase()
    if (t.includes('temperature')) return 'temperature'
    if (t.includes('humidity')) return 'humidity'
    if (t.includes('ph')) return 'ph'
    if (t.includes('ec')) return 'ec'
    if (t.includes('tds')) return 'tds'
    if (t.includes('moisture')) return 'moisture'
    if (t.includes('water flow')) return 'flow'
    return 'unknown'
}

/** Convert API reading string to a display-scale number (e.g. EC µS → mS). */
export function parseReadingToDisplayNumber(raw: string, category: MetricCategory): number | null {
    if (raw == null || raw === '' || raw === '--') return null
    const n = Number.parseFloat(String(raw))
    if (Number.isNaN(n)) return null
    if (category === 'ec') return n * 0.001
    return n
}

export function formatMetricValue(n: number, category: MetricCategory): string {
    switch (category) {
        case 'ph':
            return n.toFixed(2)
        case 'ec':
            return n.toFixed(2)
        case 'temperature':
        case 'humidity':
        case 'moisture':
            return n.toFixed(1)
        case 'tds':
            return Math.round(n).toString()
        case 'flow':
            return n.toFixed(2)
        default:
            return n.toFixed(2)
    }
}

export function unitForCategory(category: MetricCategory): string {
    switch (category) {
        case 'temperature':
            return '°F'
        case 'humidity':
        case 'moisture':
            return '%'
        case 'ec':
            return 'mS/cm'
        case 'ph':
            return ''
        case 'tds':
            return 'ppm'
        case 'flow':
            return 'L/min'
        default:
            return ''
    }
}

type ProgramShape = {
    temperature?: number
    humidity?: number
    ec?: number
    ph?: number
}

export function programTargetForCategory(system: System, category: MetricCategory): number | undefined {
    if (!(system && typeof system === 'object') || !('program' in system)) return undefined
    const p = (system as { program?: ProgramShape }).program
    if (!p) return undefined
    switch (category) {
        case 'temperature':
            return p.temperature
        case 'humidity':
            return p.humidity
        case 'ec':
            return p.ec
        case 'ph':
            return p.ph
        default:
            return undefined
    }
}

type Tolerances = {
    ph?: [number, number]
    ec?: [number, number]
    temperature?: [number, number]
    humidity?: [number, number]
}

function tolerancesKey(category: MetricCategory): keyof Tolerances | null {
    switch (category) {
        case 'ph':
            return 'ph'
        case 'ec':
            return 'ec'
        case 'temperature':
            return 'temperature'
        case 'humidity':
            return 'humidity'
        default:
            return null
    }
}

/** Band is [target - below, target + above] using stored tolerance pairs from advanced_options. */
export function toleranceBandForCategory(
    system: System,
    category: MetricCategory,
    target: number | undefined
): { low: number; high: number } | null {
    if (target === undefined || Number.isNaN(target)) return null
    const key = tolerancesKey(category)
    const adv = (system as { advanced_options?: { tolerances?: Tolerances } }).advanced_options
    const tol = key && adv?.tolerances?.[key]
    if (tol && tol.length >= 2) {
        return { low: target - tol[0], high: target + tol[1] }
    }
    const d = DEFAULT_BAND[category]
    if (d) {
        return { low: target - d.below, high: target + d.above }
    }
    return null
}

export function valueStatus(
    value: number | null,
    band: { low: number; high: number } | null
): 'ok' | 'warn' | 'empty' {
    if (value === null) return 'empty'
    if (!band) return 'ok'
    if (value < band.low || value > band.high) return 'warn'
    return 'ok'
}
